import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StateService } from './state.service';

/** Notification types for subscription matrix */
export const NOTIFICATION_TYPES = [
  'SALES',
  'LISTINGS',
  'LEGACY_BIDS',
  'PUBLIC_STASH_BIDS',
  'CONFIDENTIAL_STASH_BIDS',
  'TRANSFERS',
  'WRAP_UNWRAP_EVENTS',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** Per-type subscription state: email and/or telegram enabled */
export interface SubscriptionState {
  email?: boolean;
  telegram?: boolean;
}

/** Response for GET /api/auth/subscriptions */
export interface SubscriptionsResponse {
  subscriptions: Record<NotificationType, SubscriptionState>;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  constructor(
    private http: HttpClient,
    private stateService: StateService,
  ) {}

  private getBaseUrl(): string {
    const graphUri = environment.alternativeListingsGraphURI || '';
    return graphUri.replace(/\/graphql$/, '');
  }

  private getCollectionId(): string {
    return environment.telegramCollectionId || 'original';
  }

  private getHeaders(): { [key: string]: string } {
    const jwtToken = this.stateService.getJwtToken();
    if (!jwtToken) {
      throw new Error('User not authenticated');
    }
    return {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get notification subscriptions for the current user
   */
  async getSubscriptions(): Promise<SubscriptionsResponse> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('alternativeListingsGraphURI is not configured');
    }

    const collectionId = this.getCollectionId();
    const response = await firstValueFrom(
      this.http.get<SubscriptionsResponse>(
        `${baseUrl}/api/auth/subscriptions?collectionId=${encodeURIComponent(collectionId)}`,
        { headers: this.getHeaders() },
      ),
    );
    return response;
  }

  /**
   * Update notification subscriptions for the current user
   */
  async updateSubscriptions(
    subscriptions: Partial<Record<NotificationType, SubscriptionState>>,
  ): Promise<void> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('alternativeListingsGraphURI is not configured');
    }

    const collectionId = this.getCollectionId();
    await firstValueFrom(
      this.http.put<{ success: boolean }>(
        `${baseUrl}/api/auth/subscriptions`,
        { collectionId, subscriptions },
        { headers: this.getHeaders() },
      ),
    );
  }

  /**
   * Get per-punk minimum bid amount limits for the current user
   */
  async getBidMinAmountLimits(): Promise<{ limits: Record<string, number> }> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('alternativeListingsGraphURI is not configured');
    }

    const collectionId = this.getCollectionId();
    return firstValueFrom(
      this.http.get<{ limits: Record<string, number> }>(
        `${baseUrl}/api/auth/bid-min-amount-limits?collectionId=${encodeURIComponent(collectionId)}`,
        { headers: this.getHeaders() },
      ),
    );
  }

  /**
   * Set per-punk minimum bid amount limits for the current user
   */
  async updateBidMinAmountLimits(limits: Record<string, number>): Promise<void> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('alternativeListingsGraphURI is not configured');
    }

    const collectionId = this.getCollectionId();
    await firstValueFrom(
      this.http.put<{ success: boolean }>(
        `${baseUrl}/api/auth/bid-min-amount-limits`,
        { collectionId, limits },
        { headers: this.getHeaders() },
      ),
    );
  }
}
